<script lang="ts" setup>
import DropdownItem from '#components/elements/dropdown/DropdownItem.vue';
import DropdownMenu from '#components/elements/dropdown/DropdownMenu.vue';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useNotification } from '#composables/notifications/useNotification';
import { useLanguage } from '#composables/useLanguage';
import { usePermissions } from '#composables/usePermissions';
import { useUserStore } from '#stores/user.store';
import { PRODUCT_TAXONOMIES, type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

const { onlyCarrefour = false } = defineProps<{
  onlyCarrefour?: boolean;
}>();

const { t } = useLanguage();
const userStore = useUserStore();
const { scope } = storeToRefs(userStore);
const {
  taxonomy: { taxonomy, updateTaxonomy }
} = useKpisPerimeter();
const { hasNielsen } = usePermissions();
const { notify } = useNotification();

const hasAccessToTaxonomy = computed(
  () => scope.value?.permissions.kpis.includes('nielsenMarketShare') && hasNielsen.value
);

const taxonomies = computed(() => {
  return PRODUCT_TAXONOMIES.map((type) => ({
    value: type,
    label: type
  }));
});

const onlyCarrefourInfoMessage = computed(() => {
  if (onlyCarrefour) {
    return t('commonLabel.taxonomy.onlyInCarrefour');
  }

  return undefined;
});

const handleTaxonomyChange = (value: ProductTaxonomyType) => {
  updateTaxonomy(value);

  const capitalizeTaxonomy = value.charAt(0).toUpperCase() + value.slice(1);
  const title = `${t('commonLabel.taxonomy')} ${capitalizeTaxonomy}`;
  const message =
    value === 'carrefour' ? t('commonLabel.defaultFilterPerimeterIsReset') : t('commonLabel.reEnterFilters');

  notify({
    title,
    body: message,
    status: 'info',
    autoclose: true
  });
};
</script>

<template>
  <DropdownMenu
    v-if="hasAccessToTaxonomy"
    :no-padding="true"
    background="outline"
    variant="default"
    :disabled="onlyCarrefour"
    :popover="onlyCarrefourInfoMessage"
  >
    <span class="text-default-disabled"> {{ t('commonLabel.taxonomy') }} </span>
    <span class="capitalize">{{ taxonomy }}</span>

    <template #items>
      <DropdownItem v-for="item in taxonomies" :key="item.value" @click="handleTaxonomyChange(item.value)">
        <button v-if="item.value !== taxonomy" class="capitalize">
          {{ item.label }}
        </button>
        <strong v-else class="capitalize">{{ item.label }} </strong>
      </DropdownItem>
    </template>
  </DropdownMenu>
</template>
